'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus, User, Loader2, Search } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import { useOrgMemberships } from '@/contexts/org-memberships-context';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetAccountInfo } from '@/lib/api/account';
import { useCloakSanitizer } from '@/lib/cloak-sanitize';
import { CopyableResourceBadge } from './copyable-resource-badge';

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const OrganizationAvatar = React.memo(function OrganizationAvatar({
  name,
  size = 'h-6 w-6',
  className = '',
}: {
  name: string;
  size?: string;
  className?: string;
}) {
  return (
    <div
      className={`${size} ${className} bg-primary text-primary-foreground flex shrink-0 items-center justify-center rounded-md text-xs font-medium`}
    >
      {getInitials(name)}
    </div>
  );
});

type OrganizationSwitcherProps = {
  /** Compact layout for the top header (sidebar collapsed / blocking pages). */
  compact?: boolean;
  /** Higher-contrast trigger for gate pages where switching must be obvious. */
  prominent?: boolean;
  /** Popover alignment — `end` when the trigger sits on the right side of the header. */
  align?: 'start' | 'end';
};

export const OrganizationSwitcher = React.memo(function OrganizationSwitcher({
  compact = false,
  prominent = false,
  align = 'start',
}: OrganizationSwitcherProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSwitching, setIsSwitching] = React.useState(false);
  const { organization } = useOrganization();
  const { userMemberships, isLoaded, loadAllPages } = useOrgMemberships();
  const navigate = useNavigate();
  const { data: accountInfo } = useGetAccountInfo();
  const cloak = useCloakSanitizer();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const organizations = React.useMemo(() => {
    if (!isLoaded || !userMemberships?.data?.length) return [];

    const query = searchQuery.toLowerCase().trim();

    return userMemberships.data
      .filter((membership) => {
        if (!query) return true;
        const org = membership.organization;
        return (
          org.name.toLowerCase().includes(query) ||
          (org.slug && org.slug.toLowerCase().includes(query)) ||
          org.id.toLowerCase().includes(query)
        );
      })
      .map((membership) => ({
        id: membership.organization.id,
        name: membership.organization.name,
        slug: membership.organization.slug,
        role: membership.role,
      }));
  }, [isLoaded, userMemberships?.data, searchQuery]);

  const getRoleText = (role: string): string => {
    return role.replace('org:', '').replace('_', ' ');
  };

  const handleCreateOrganization = () => {
    window.location.href = '/create-account';
  };

  const handleOrganizationSwitch = (organizationId: string, slug: string) => {
    if (organization?.id === organizationId) {
      setOpen(false);
      return;
    }
    setIsSwitching(true);
    setOpen(false);
    navigate(`/org/${slug}`);
  };

  const handleOpenChange = (next: boolean) => {
    if (isSwitching) return;
    if (next) loadAllPages();
    setOpen(next);
  };

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setIsSwitching(false);
    }
  }, [open]);

  const triggerClassName = compact
    ? prominent
      ? 'border-border bg-background hover:bg-accent flex h-9 w-auto max-w-[220px] items-center gap-2 rounded-lg border px-3 text-left shadow-sm transition-colors disabled:opacity-50'
      : 'bg-muted/50 hover:bg-muted flex h-8 w-auto max-w-[200px] items-center gap-2 rounded-full px-2.5 text-left transition-colors disabled:opacity-50'
    : 'hover:bg-accent flex h-9 w-full items-center gap-2 rounded-md px-2 text-left transition-colors disabled:opacity-50';

  if (!isLoaded) {
    return (
      <div className={`flex items-center gap-2 px-2 ${compact ? 'w-auto' : 'w-full'}`}>
        <Skeleton className="h-6 w-6 rounded-md" />
        <Skeleton className="h-4 w-[100px]" />
      </div>
    );
  }

  if (!userMemberships.isLoading && !userMemberships?.data?.length) {
    return (
      <Button
        variant="outline"
        onClick={handleCreateOrganization}
        className={`justify-between px-2 focus-visible:ring-0 focus-visible:ring-offset-0 ${compact ? 'w-auto max-w-[220px]' : 'w-full'}`}
      >
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarFallback>
              <User className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <span className="text-muted-foreground truncate">{t('organizationSwitcher.createAccount')}</span>
        </div>
        <Plus className="ml-auto h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={triggerClassName}
          disabled={isSwitching}
        >
          {isSwitching ? (
            <div className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
              <Loader2 className="text-primary-foreground h-3.5 w-3.5 animate-spin" />
            </div>
          ) : (
            <OrganizationAvatar
              name={cloak(organization?.name) || 'Account'}
              size="h-6 w-6"
            />
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {isSwitching ? t('organizationSwitcher.switching') : cloak(organization?.name) || 'Account'}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align={align} sideOffset={8}>
        {/* Search */}
        <div className="border-b px-3 py-2">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('organizationSwitcher.searchPlaceholder')}
              className="placeholder:text-muted-foreground h-8 w-full rounded-md bg-transparent pl-7 pr-2 text-sm outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Account ID */}
        {accountInfo?.id && (
          <div className="border-b px-3 py-1.5">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">{t('organizationSwitcher.accountId')}</span>
              <CopyableResourceBadge
                name="Account ID"
                value={accountInfo.id}
                className="text-muted-foreground hover:text-foreground h-auto border-0 bg-transparent p-0 font-mono text-[10px]"
                variant="outline"
              />
            </div>
          </div>
        )}

        {/* Org list */}
        <div className="max-h-[280px] overflow-y-auto py-1">
          {organizations.length === 0 && searchQuery ? (
            <div className="text-muted-foreground px-3 py-6 text-center text-sm">
              {t('organizationSwitcher.noOrganizationsFound')}
            </div>
          ) : (
            organizations.map((org) => {
              const isActive = organization?.id === org.id;
              return (
                <button
                  key={org.id}
                  onClick={() => {
                    if (org.slug && !isSwitching) {
                      handleOrganizationSwitch(org.id, org.slug);
                    }
                  }}
                  disabled={isSwitching || !org.slug}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50 ${isActive ? 'bg-accent/50' : ''
                    }`}
                >
                  <OrganizationAvatar name={cloak(org.name)} size="h-7 w-7" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{cloak(org.name)}</span>
                    <span className="text-muted-foreground truncate font-mono text-[10px]">
                      {cloak(org.slug)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className="text-muted-foreground rounded bg-muted px-1 py-0.5 text-[10px] capitalize">
                      {getRoleText(org.role)}
                    </span>
                    {isActive && <Check className="text-primary h-3.5 w-3.5" />}
                  </div>
                </button>
              );
            })
          )}
          {userMemberships?.isFetching && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
            </div>
          )}
        </div>

        {/* Create */}
        <div className="border-t px-1 py-1">
          <button
            onClick={handleCreateOrganization}
            className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors"
          >
            <Plus className="h-4 w-4 opacity-60" />
            {t('organizationSwitcher.createAccount')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
});
